import React, { useCallback, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import SydButton from '../../../../../../../commonDesign/Button'
import SydModalActions from '../../../../../../../commonDesign/SydModal/SydModalActions'
import { dangerZone } from '../../../../../../../../api/coreData'
import { decipherError } from '../../../../../../../../utils/decipherError'
import FilterFieldEditor from '../FilterFieldEditor'
import SydLabel, { hookFormErrorAdapter } from '../../../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../../../commonDesign/SydInput'

const useSubmitter = (form, onComplete, report, filter, filterComposition, groupTag) => {
  const { handleSubmit } = form
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: modifyReportFilter } = dangerZone.__useModifyDenaliReportFilter()
  const onSubmit = useCallback(async (formData) => {
    const command = {
      reportId: report.reportId,
      filterId: filter.firmReportFilterId,
      filter: filterComposition,
      filterGroupTag: formData.filterGroupTag || null
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await modifyReportFilter(command)
      if (result?.statusCode === 500) {
        throw new Error('Failed to modify report columns')
      }
      // eslint-disable-next-line no-debugger
      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [setProcessing, onComplete, setError, modifyReportFilter, report, filter, filterComposition])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = (errors) => {
      console.error(errors)
    }

    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, onSubmit])

  return {
    submitter,
    processing,
    error
  }
}

function EditFilterForm ({ filter, report, onCancel, onComplete }) {
  const [_filter, setFilter] = useState(filter?.filter)
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      filterGroupTag: filter.filterGroupTag || 'default'
    }
  })
  const { submitter, processing, error } = useSubmitter(form, onComplete, report, filter, _filter)

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Controller
          name='filterGroupTag'
          control={form.control}
          rules={{
            required: { value: true, message: 'Required' },
            maxLength: { value: 64, message: 'Max 64 Characters' },
            pattern: { value: /^[a-z][a-z0-9_]{0,63}$/, message: 'Only lowercase characters, numbers and _ allowed' }
          }}
          render={(f) => (
            <SydLabel required label='Filter Group Tag' description='Max 64 Characters' error={hookFormErrorAdapter(form, f.fieldState)}>
              <SydInput {...f.field} />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <FilterFieldEditor value={_filter} columns={report.configuration?.columns} onChange={setFilter} />
      </Grid>
      <Grid item xs={12}>
        <SydModalActions>
          {error ? (<div className='__error'>{error}</div>) : null}
          <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>Cancel</SydButton>
          <SydButton
            processing={processing}
            variant='primary'
            size='md'
            onClick={submitter}
          >
            Save
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

EditFilterForm.propTypes = {
  report: PropTypes.object,
  filter: PropTypes.object,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

export default EditFilterForm
